import { photos, type SitePhoto } from "@/lib/photos";

/** A built-in optimized photo, or a photo Ella uploaded in the studio. */
export type CollectionPhoto = {
  src: string;
  avif?: string;
  width: number;
  height: number;
  variants?: SitePhoto["variants"];
};

export type CollectionItem = {
  slug: string;
  name: string;
  photo: CollectionPhoto;
  alt: string;
  /** Short line for home previews and compact cards. */
  copy: string;
  /** Longer collection-page description. */
  detail: string;
  /** What comes with the piece. */
  includes: string[];
  /** Knit, crochet, or sewn. */
  craft: string;
  /** One line on the occasion it suits. */
  occasion: string;
  featured?: boolean;
};

/**
 * Handmade pieces currently on the site.
 * Add new items here — Home features items with `featured: true`,
 * and Collection renders the full list in this order.
 */
export const COLLECTION: CollectionItem[] = [
  {
    slug: "coral-crochet-dress-set",
    name: "Coral Crochet Dress Set",
    photo: photos.coral,
    alt: "Handmade coral crochet doll dress with white scalloped trim, matching rose headband and white booties",
    copy: "A coral crochet dress with scalloped white edging, plus a rosette headband and booties.",
    detail:
      "A coral crochet dress with scalloped white edging, finished with a matching rosette headband and booties. Soft, gift-ready, and made by hand for 18-inch dolls — a cheerful everyday look that still feels special when the box is opened.",
    includes: ["Crochet dress", "Rosette headband", "White booties"],
    craft: "Crocheted by hand",
    occasion: "Birthdays and everyday dress-up, when you want color that still feels special.",
    featured: true,
  },
  {
    slug: "cream-heirloom-set",
    name: "Cream Heirloom Set",
    photo: photos.cream,
    alt: "Cream crochet doll gown with pink ribbon, matching bonnet, lace slip and booties",
    copy: "Cream gown, bonnet, lace-trimmed slip, and booties finished with soft pink ribbon.",
    detail:
      "A cream gown with a matching bonnet, lace-trimmed slip, and booties, all finished with soft pink ribbon. An heirloom-style set meant to be treasured — boutique quality with the warmth of something stitched for a grandchild.",
    includes: ["Cream gown", "Matching bonnet", "Lace-trimmed slip", "Booties", "Pink ribbon trim"],
    craft: "Crocheted and sewn by hand",
    occasion: "Christenings, keepsake photos, and heirloom gifts from a grandparent.",
    featured: true,
  },
  {
    slug: "purple-ripple-dress",
    name: "Purple Ripple Dress",
    photo: photos.purple,
    alt: "Hand-crocheted purple and white ripple-stitch doll dress on a hanger",
    copy: "A cheerful ripple-stitch dress in violet, lavender, and white.",
    detail:
      "A cheerful ripple-stitch dress in violet, lavender, and white. Lightweight and easy for play, with the kind of handmade texture you can see in every row of stitches — a sweet splash of color for an 18-inch doll.",
    includes: ["Ripple-stitch dress"],
    craft: "Crocheted by hand",
    occasion: "Everyday play and a just-because gift in a child's favorite color.",
  },
  {
    slug: "buttercup-dress-headband",
    name: "Buttercup Dress & Headband",
    photo: photos.yellow,
    alt: "White crochet doll dress with yellow trimmed yoke and matching yellow headband",
    copy: "Soft white crochet with a sunny yellow yoke and a matching headband.",
    detail:
      "Soft white crochet with a sunny yellow yoke and a matching headband. Bright, sweet, and ready for everyday dress-up — a simple look that still feels thoughtfully made, stitch by stitch.",
    includes: ["Crochet dress", "Matching headband"],
    craft: "Crocheted by hand",
    occasion: "Easter and spring birthdays, or any sunny-day outfit change.",
  },
  {
    slug: "bridal-gown-veil",
    name: "Bridal Gown & Veil",
    photo: photos.bridal,
    alt: "White crochet doll bridal gown with satin bow, tulle veil and pink flower crown",
    copy: "A crocheted wedding gown with satin bow, lace-edged veil, and floral crown.",
    detail:
      "A crocheted wedding gown with a satin bow, lace-edged veil, and floral crown. A special-occasion piece for pretend weddings, keepsake photos, and the kind of gift that gets remembered long after the wrapping paper is gone.",
    includes: ["Crochet gown", "Satin bow", "Lace-edged tulle veil", "Flower crown"],
    craft: "Crocheted and sewn by hand",
    occasion: "Pretend weddings and flower-girl play, or a keepsake gift to mark a family wedding.",
    featured: true,
  },
  {
    slug: "bride-doll-ensemble",
    name: "Bride Doll Ensemble",
    photo: photos.dollBridal,
    alt: "18-inch doll wearing a handmade ivory bridal gown with lace appliqué and a flower crown veil",
    copy: "Lace-appliqué satin gown styled complete with veil — a keepsake special-occasion look.",
    detail:
      "A lace-appliqué satin gown styled complete with veil — shown as a finished look on the doll. A keepsake special-occasion ensemble for celebrations, portraits, and one very lucky 18-inch doll.",
    includes: ["Lace-appliqué gown", "Floral veil", "Styled as a complete look"],
    craft: "Sewn by hand with crochet detailing",
    occasion: "Celebrations, portraits, and display pieces meant to be kept.",
  },
  {
    slug: "white-lace-gown-flower-crown-veil",
    name: "White Lace Gown & Flower Crown Veil",
    photo: photos.dollCommunion,
    alt: "18-inch doll wearing a white crochet lace gown with satin ribbon sash, tulle veil and pink and white flower crown",
    copy: "An openwork crochet gown with satin sash, tulle veil, and a pink-and-white flower crown.",
    detail:
      "A white openwork crochet gown with a satin ribbon sash, shown with a long tulle veil and a pink-and-white flower crown. A soft, formal look for special-occasion play, portraits, and keepsake gifting — made by hand for 18-inch dolls.",
    includes: ["Crochet lace gown", "Satin ribbon sash", "Tulle veil", "Flower crown"],
    craft: "Crocheted by hand with sewn finishing",
    occasion: "Communions, flower-girl play, pretend weddings, and keepsake photographs.",
  },
];

export const FEATURED_COLLECTION = COLLECTION.filter((item) => item.featured);

export function findCollectionItem(slug: string | undefined) {
  if (!slug) return undefined;
  return COLLECTION.find((item) => item.slug === slug);
}
